// Every kind of dish the pit can see. A restaurant's setup says which ones it
// uses and how many of each it owns. A dirty one comes back with a few layers
// of grime (`grime`, rolled each time), and each layer takes `scrubSeconds`
// of holding the sponge on it: a mug's coffee ring comes off fast, a wok's
// burnt-on grease takes a while.

export type DishKind = "plate" | "mug" | "bowl" | "cup" | "sauce" | "wok" | "basket" | "tumbler" | "shotglass" | "pitcher";

export interface DishSpec {
  name: string;
  plural: string;
  /** Layers of grime a dirty one comes back with: [fewest, most]. */
  grime: [number, number];
  /** Seconds of scrubbing each layer takes. */
  scrubSeconds: number;
  /** What's stuck to it. */
  mess: string;
  /** Never goes in the machine: scrubbed clean in the sink, then carried straight to the shelf. */
  handWash?: boolean;
  /**
   * Kitchen cookware, not tableware: it comes back from the line this many
   * seconds after the order goes out, not when the table's done eating.
   */
  backFromLine?: number;
}

export const DISHES: Record<DishKind, DishSpec> = {
  plate: { name: "Plate", plural: "plates", grime: [1, 2], scrubSeconds: 0.7, mess: "egg yolk and syrup" },
  mug: { name: "Mug", plural: "mugs", grime: [1, 1], scrubSeconds: 0.6, mess: "a coffee ring" },
  bowl: { name: "Bowl", plural: "bowls", grime: [2, 3], scrubSeconds: 0.7, mess: "stuck-on noodles and oats" },
  cup: { name: "Tea cup", plural: "tea cups", grime: [1, 1], scrubSeconds: 0.5, mess: "a tea stain" },
  sauce: { name: "Sauce dish", plural: "sauce dishes", grime: [1, 1], scrubSeconds: 0.5, mess: "soy and chili oil" },
  wok: { name: "Wok", plural: "woks", grime: [3, 4], scrubSeconds: 1.8, mess: "burnt-on grease", handWash: true, backFromLine: 7 },
  basket: { name: "Basket", plural: "baskets", grime: [1, 2], scrubSeconds: 0.6, mess: "ketchup and grease" },
  // The Burger Shack's soda cups: plastic tumblers, no lids or straws.
  tumbler: { name: "Cup", plural: "cups", grime: [1, 1], scrubSeconds: 0.45, mess: "sticky soda" },
  // The coffee truck's: a shot glass is dirty the moment its shot's poured, a
  // milk pitcher once its milk is. No machine in a truck: rinsed by hand.
  shotglass: { name: "Shot glass", plural: "shot glasses", grime: [1, 1], scrubSeconds: 0.4, mess: "espresso crema", handWash: true },
  pitcher: { name: "Milk pitcher", plural: "milk pitchers", grime: [1, 2], scrubSeconds: 0.55, mess: "milk film", handWash: true },
};

/** Display order: shelves, the HUD, the briefing. */
export const DISH_ORDER: readonly DishKind[] = ["basket", "tumbler", "shotglass", "pitcher", "plate", "mug", "bowl", "cup", "sauce", "wok"];
